import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import { errorNotification } from "../../utils/helpers";

interface Episode {
  id: number;
  title: string;
  summary?: string | null;
  description_html?: string | null;
  published_at: string;
  duration_seconds?: number | null;
  duration_label?: string | null;
  audio_url: string;
  episode_url?: string | null;
  image_url?: string | null;
}

interface Pagination {
  current_page: number;
  next_page?: number;
  total_pages: number;
  total_count: number;
}

interface EpisodesResponse {
  episodes: Episode[];
  total_count: number;
  pagination: Pagination;
}

interface State {
  loading: boolean;
  searching: boolean;
  episodes: Episode[];
  totalCount: number;
  pagination: Pagination;
  query: string;
  expanded: Record<number, boolean>;
  playingId?: number;
}

const SEARCH_DEBOUNCE_MS = 350;
const APPLE_URL = "https://podcasts.apple.com/gb/podcast/42courses-podcast/id1481001984";
const SPOTIFY_URL = "https://open.spotify.com/show/0G3ZhYVoVIKWVbbATFsm6X";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const pageNumbers = (current: number, total: number): (number | "gap")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const around = [current - 1, current, current + 1].filter(
    (n) => n > 1 && n < total,
  );
  const numbers: (number | "gap")[] = [1];
  if (around[0] > 2) numbers.push("gap");
  numbers.push(...around);
  if (around[around.length - 1] < total - 1) numbers.push("gap");
  numbers.push(total);

  return numbers;
};

const readUrlState = (): { query: string; page: number } => {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get("page") || "1", 10);

  return {
    query: params.get("q") || "",
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
};

// Every episode of the 42courses Podcast, playable in place. The audio plays
// from wherever the show's feed points, so the site stores only the words.
class PodcastsIndex extends Component<{}, State> {
  private requestSeq = 0;
  private searchTimer?: number;
  private listRef = React.createRef<HTMLDivElement>();

  state: State = {
    loading: true,
    searching: false,
    episodes: [],
    totalCount: 0,
    pagination: { current_page: 1, total_pages: 1, total_count: 0 },
    query: readUrlState().query,
    expanded: {},
  };

  componentDidMount() {
    document.title = "42courses Podcast | Every episode, playable here";
    this.fetchEpisodes(readUrlState().page);
  }

  componentWillUnmount() {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }
  }

  syncUrl = (page: number) => {
    const params = new URLSearchParams();
    if (this.state.query.trim()) params.set("q", this.state.query.trim());
    if (page > 1) params.set("page", String(page));
    const search = params.toString();

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}`,
    );
  };

  fetchEpisodes = (page = 1, scrollToList = false) => {
    const seq = ++this.requestSeq;
    const query = this.state.query.trim();
    const params = [`page=${page}`];
    if (query) params.push(`q=${encodeURIComponent(query)}`);

    this.setState({ searching: true });
    this.syncUrl(page);

    getRequest(`/api/podcast-episodes.json?${params.join("&")}`)
      .then((response: AxiosResponse<EpisodesResponse>) => {
        if (seq !== this.requestSeq) return;

        const { episodes, total_count, pagination } = response.data;

        this.setState(
          {
            episodes,
            totalCount: total_count,
            pagination,
            loading: false,
            searching: false,
            expanded: {},
          },
          () => {
            if (scrollToList && this.listRef.current) {
              this.listRef.current.scrollIntoView({ block: "start" });
            }
          },
        );
      })
      .catch((error) => {
        if (seq !== this.requestSeq) return;

        this.setState({ loading: false, searching: false });
        errorNotification(
          "Error loading the podcast",
          JSON.stringify(error.response?.data || error.message),
        );
      });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ query: event.target.value });

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(
      () => this.fetchEpisodes(1),
      SEARCH_DEBOUNCE_MS,
    );
  };

  handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.fetchEpisodes(1);
  };

  goToPage = (page: number) => {
    const { current_page, total_pages } = this.state.pagination;
    if (page === current_page || page < 1 || page > total_pages) return;

    this.fetchEpisodes(page, true);
  };

  toggleExpanded = (id: number) => {
    this.setState((state) => ({
      expanded: { ...state.expanded, [id]: !state.expanded[id] },
    }));
  };

  // One thing playing at a time: starting an episode pauses the others.
  handlePlay = (id: number, event: React.SyntheticEvent<HTMLAudioElement>) => {
    const current = event.currentTarget;
    document
      .querySelectorAll<HTMLAudioElement>(".podcasts-page__player audio")
      .forEach((player) => {
        if (player !== current) player.pause();
      });
    this.setState({ playingId: id });
  };

  renderEpisode = (episode: Episode) => {
    const expanded = Boolean(this.state.expanded[episode.id]);
    const hasMore =
      Boolean(episode.description_html) &&
      (episode.summary || "").length >= 200;

    return (
      <li
        className={`podcasts-page__episode ${
          this.state.playingId === episode.id ? "is-playing" : ""
        }`}
        key={episode.id}
      >
        <div className="podcasts-page__episode-meta">
          <time dateTime={episode.published_at}>
            {formatDate(episode.published_at)}
          </time>
          {episode.duration_label && (
            <span className="podcasts-page__duration">
              {episode.duration_label}
            </span>
          )}
        </div>
        <h3 className="podcasts-page__title">{episode.title}</h3>

        {expanded && episode.description_html ? (
          <div
            className="podcasts-page__notes"
            dangerouslySetInnerHTML={{ __html: episode.description_html }}
          />
        ) : (
          episode.summary && (
            <p className="podcasts-page__summary">{episode.summary}</p>
          )
        )}
        {hasMore && (
          <button
            type="button"
            className="podcasts-page__more"
            onClick={() => this.toggleExpanded(episode.id)}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show notes"}
          </button>
        )}

        <div className="podcasts-page__player">
          <audio
            controls
            preload="none"
            src={episode.audio_url}
            onPlay={(event) => this.handlePlay(episode.id, event)}
            aria-label={`Play ${episode.title}`}
          >
            <a href={episode.audio_url}>Download the audio</a>
          </audio>
          {episode.episode_url && (
            <a
              className="podcasts-page__episode-link"
              href={episode.episode_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open on Spotify →
            </a>
          )}
        </div>
      </li>
    );
  };

  renderPagination = () => {
    const { current_page, total_pages, total_count } = this.state.pagination;
    if (total_pages <= 1) return null;

    return (
      <nav className="podcasts-page__pages" aria-label="Episode pages">
        <button
          type="button"
          className="podcasts-page__page-step"
          onClick={() => this.goToPage(current_page - 1)}
          disabled={current_page <= 1}
        >
          ← Newer
        </button>
        <ol className="podcasts-page__page-list">
          {pageNumbers(current_page, total_pages).map((entry, index) =>
            entry === "gap" ? (
              <li
                key={`gap-${index}`}
                className="podcasts-page__page-gap"
                aria-hidden="true"
              >
                …
              </li>
            ) : (
              <li key={entry}>
                <button
                  type="button"
                  className={`podcasts-page__page ${
                    entry === current_page ? "is-current" : ""
                  }`}
                  aria-current={entry === current_page ? "page" : undefined}
                  onClick={() => this.goToPage(entry)}
                >
                  {entry}
                </button>
              </li>
            ),
          )}
        </ol>
        <button
          type="button"
          className="podcasts-page__page-step"
          onClick={() => this.goToPage(current_page + 1)}
          disabled={current_page >= total_pages}
        >
          Older →
        </button>
        <p className="podcasts-page__page-count">
          Page {current_page} of {total_pages} · {total_count} episodes
        </p>
      </nav>
    );
  };

  renderEmpty = () => {
    const { searching, query, totalCount } = this.state;

    if (searching) {
      return (
        <div className="ui active centered inline loader podcasts-page__loader" />
      );
    }

    if (totalCount === 0) {
      return (
        <p>
          The episodes are being fetched from the podcast feed right now. Give
          it a minute and reload.
        </p>
      );
    }

    return (
      <p>
        Nothing found{query.trim() ? <> for “{query.trim()}”</> : null}. Try a
        guest's name or a topic.
      </p>
    );
  };

  render() {
    const { loading, episodes, query, totalCount, searching } = this.state;

    if (loading) {
      return (
        <main className="podcasts-page">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        </main>
      );
    }

    const artwork = episodes[0]?.image_url;

    return (
      <div className="podcasts-page">
        <header className="podcasts-page__hero">
          <div className="podcasts-page__hero-inner">
            {artwork && (
              <img
                className="podcasts-page__artwork"
                src={artwork}
                alt="42courses Podcast artwork"
                width="160"
                height="160"
              />
            )}
            <div className="podcasts-page__hero-copy">
              <p className="podcasts-page__kicker">The 42courses Podcast</p>
              <h1 className="podcasts-page__intro">
                Conversations with the people who <em>wrote the book</em>.
              </h1>
              <p className="podcasts-page__sub">
                Behavioural science, creativity and business, with the likes of
                Rory Sutherland, Dan Ariely and Meik Wiking.
                {totalCount > 0 && <> {totalCount} episodes, all playable here.</>}
              </p>
              <div className="podcasts-page__listen">
                <a
                  href={APPLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="podcasts-page__listen-link"
                >
                  Apple Podcasts
                </a>
                <a
                  href={SPOTIFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="podcasts-page__listen-link"
                >
                  Spotify
                </a>
              </div>
            </div>
          </div>

          <form
            className="podcasts-page__search"
            role="search"
            onSubmit={this.handleSearchSubmit}
          >
            <i className="search icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={this.handleSearchChange}
              placeholder="Search by guest or topic…"
              aria-label="Search episodes"
            />
          </form>
        </header>

        <main
          className={`podcasts-page__main ${searching ? "is-searching" : ""}`}
          ref={this.listRef}
        >
          {episodes.length === 0 ? (
            <div className="podcasts-page__empty">{this.renderEmpty()}</div>
          ) : (
            <>
              <ol className="podcasts-page__list">
                {episodes.map(this.renderEpisode)}
              </ol>
              {this.renderPagination()}
            </>
          )}
        </main>
      </div>
    );
  }
}

export default PodcastsIndex;
